import type { IUserProfile } from 'src/types/user';

import { mutate } from 'swr';

import { Stack, Button, Dialog, DialogContent } from '@mui/material';

import { getEndpoints } from 'src/utils/axios';

import { useDeleteImageForUser } from 'src/actions/image';
import {
  useUpdateUserProfile,
  useCreateUserProfileImage,
} from 'src/actions/user';

import { ProfileView } from 'src/components/profile';


export function UserProfileDialog({
  open,
  onClose,
  userProfile,
}: {
  open: boolean;
  onClose: () => void;
  userProfile: IUserProfile;
}) {
  // Hooks for user profile updates
  const { createUserProfileImage, isCreating } = useCreateUserProfileImage(userProfile.guid);
  const { updateUserProfile, isUpdating } = useUpdateUserProfile(userProfile.guid);

  const { deleteImageForUser, isDeleting } = useDeleteImageForUser(
    userProfile.guid,
    userProfile.id,
    userProfile.image?.guid || ''
  );

  // Handle profile update
  const handleUpdateProfile = async (data: any) => {
    try {
      await updateUserProfile({
        bio: data.bio,
        tagline: data.tagline,
        displayName: data.displayName,
        webURL: data.webURL,
      });
      return true;
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  };

  // Handle image deletion
  const handleDeleteImage = async () => {
    try {
      await deleteImageForUser();
      // Also update the SWR cache for the user profile endpoint
      await mutate(getEndpoints.user.byGuid(userProfile.guid), undefined, {
        revalidate: true,
      });
      return true;
    } catch (error) {
      console.error('Failed to delete image:', error);
      throw error;
    }
  };

  // Using the reusable profile components within the original dialog structure
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Stack direction="row" justifyContent="flex-end" pt={2} pr={2}>
        <Button onClick={onClose}>Close</Button>
      </Stack>
      <DialogContent sx={{ p: 2 }}>
        <ProfileView
          profile={userProfile}
          addFirstImage={{
            execute: createUserProfileImage,
            loading: isCreating,
          }}
          onUpdateProfile={handleUpdateProfile}
          onImageUpdated={async () => {
            // Mutate only the user profile endpoint to refresh the image
            await mutate(getEndpoints.user.byGuid(userProfile.guid), undefined, {
              revalidate: true,
            });
          }}
          onDeleteImage={handleDeleteImage}
          isUpdatingProfile={isUpdating}
          isDeletingImage={isDeleting}
          disableNameEdit
        />
      </DialogContent>
    </Dialog>
  );
}
